import classNames from "classnames";
import React, { type JSX, useState } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconSliderLeft from "../../../../img/icons/slider-left.svg";
import iconSliderRight from "../../../../img/icons/slider-right.svg";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IWebPageURL } from "../../../models/nominals";
import { ICompareTile, ITileID } from "../../../models/product-compare";
import { SelectedProduct } from "../elements/SelectedProduct";
import { getSelectedVariant } from "../selectors";

import styles from "./SelectedProductTray.module.scss";

const MOBILE_WIDTH_THRESHOLD = 501;

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return window ? window.innerWidth < MOBILE_WIDTH_THRESHOLD : false;
};

const buildCompareNowURI = (
    baseURL: IWebPageURL,
    selectedTiles: readonly ICompareTile[],
): string => {
    const window = getWindow();
    const tileIDs = selectedTiles.map((tile) => tile.product_compare_tile.id);
    const returnURL = window
        ? encodeURIComponent(window.location.pathname)
        : "";
    const ids = encodeURIComponent(tileIDs.join(","));
    return `${baseURL}?tiles=${ids}&return_url=${returnURL}`;
};

const buildAttributes = (
    selectedTiles: readonly ICompareTile[],
): { ariaHidden: boolean } => {
    if (selectedTiles.length > 0) {
        return {
            ariaHidden: false,
        };
    } else {
        return {
            ariaHidden: true,
        };
    }
};

const buildSelectedProducts = (
    selectedTiles: readonly ICompareTile[],
    priceSelectValue: string,
    onRemoveButtonClick: (tileID: ITileID) => void,
    isExpanded: boolean,
    numColumns: number,
    isMobile: boolean,
    activeIndex: number,
    onNextClick: () => void,
    onPrevClick: () => void,
): React.JSX.Element[] | React.JSX.Element => {
    const displayArrows = selectedTiles.length > 2;
    const selectedProducts = selectedTiles.map((tile) => {
        const variant = getSelectedVariant(tile, priceSelectValue);
        return (
            <SelectedProduct
                key={`${tile.product_compare_tile.id}`}
                data={tile}
                selectedVariant={variant}
                onRemoveButtonClick={onRemoveButtonClick}
                priceSelectValue={priceSelectValue}
            />
        );
    });
    while (selectedProducts.length < numColumns) {
        const placeholderClasses = classNames({
            [styles.placeholder]: true,
            [styles.placeholderCollapsed]: !isExpanded,
        });
        selectedProducts.push(
            <div key={Math.random()} className={placeholderClasses}>
                <span className="ada-screenreader-only">
                    {t`Compare product placeholder`}
                </span>
            </div>,
        );
    }

    if (isMobile) {
        const sliderStyle = {
            transform: `translateX(${displayArrows ? activeIndex * -100 : 0}%)`,
            transition: "0.2s",
        };

        const leftButtonClass = classNames({
            "left-button": true,
            "active": activeIndex,
        });

        const rightButtonClass = classNames({
            "right-button": true,
            "active": !activeIndex,
        });

        const arrows: JSX.Element = (
            <div className={styles.slideControls}>
                <button aria-label={t`View Previous`} onClick={onPrevClick}>
                    <SVG
                        aria-hidden="true"
                        className={leftButtonClass}
                        src={iconSliderLeft}
                    />
                </button>
                <button aria-label={t`View Next`} onClick={onNextClick}>
                    <SVG
                        aria-hidden="true"
                        className={rightButtonClass}
                        src={iconSliderRight}
                    />
                </button>
            </div>
        );

        return (
            <>
                <div className={styles.slideContainer} style={sliderStyle}>
                    {selectedProducts}
                </div>
                {displayArrows && arrows}
            </>
        );
    }

    return selectedProducts;
};

export const SelectedProductTray = (props: {
    selectedTiles: readonly ICompareTile[];
    compareAllURI: string;
    priceSelectValue: string;
    productComparePageURL: IWebPageURL | null;
    onRemoveButtonClick: (tileID: ITileID) => void;
    onCollapseToggle: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isExpanded: boolean;
    ariaLabel: string;
    numProductsAvailable: number;
    ctaText: string;
}): React.ReactNode => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [numColumns, setNumColumns] = useState(4);

    useMountEffect(() => {
        const window = getWindow();

        const checkMobileState = (): void => {
            const mobile: boolean = isMobileWidth() ? true : false;
            setIsMobile(mobile);
        };

        checkMobileState();
        if (window) {
            window.addEventListener("resize", checkMobileState);
        }

        setNumColumns(
            props.numProductsAvailable > 4 ? 4 : props.numProductsAvailable,
        );

        return () => {
            if (window) {
                window.removeEventListener("resize", checkMobileState);
            }
        };
    });

    const onCollapseToggle = (e: React.MouseEvent<HTMLButtonElement>): void => {
        props.onCollapseToggle(e);
    };

    const onNextClick = (): void => {
        if (activeIndex < 1) {
            setActiveIndex(activeIndex + 1);
        }
    };

    const onPrevClick = (): void => {
        if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }
    };

    const attrs = buildAttributes(props.selectedTiles);
    const toggleButtonText =
        props.ariaLabel.charAt(0).toUpperCase() + props.ariaLabel.substring(1);
    let button: JSX.Element | null = null;
    if (props.productComparePageURL) {
        button = (
            <a
                href={buildCompareNowURI(
                    props.productComparePageURL,
                    props.selectedTiles,
                )}
                className={`${styles.navButton} button al-comparetray-comparenow`}
            >
                {t`Compare Now`}
            </a>
        );
    }

    const productTrayClass = classNames({
        [styles.hidden]: attrs.ariaHidden,
        [styles.root]: !attrs.ariaHidden,
        [styles.collapsed]: !props.isExpanded,
        [styles.numColumns2]: numColumns === 2,
        [styles.numColumns3]: numColumns === 3,
    });

    return (
        <section
            className={productTrayClass}
            aria-expanded={props.isExpanded}
            aria-hidden={attrs.ariaHidden}
        >
            {buildSelectedProducts(
                props.selectedTiles,
                props.priceSelectValue,
                props.onRemoveButtonClick,
                props.isExpanded,
                numColumns,
                isMobile,
                activeIndex,
                onNextClick,
                onPrevClick,
            )}
            <nav>
                {button}
                {props.productComparePageURL && (
                    <a
                        href={props.compareAllURI}
                        className="al-comparetray-compareall"
                    >
                        {props.ctaText}
                    </a>
                )}
                <button
                    type="button"
                    aria-label={props.ariaLabel}
                    className={`al-comparetray-${props.ariaLabel}`}
                    onClick={onCollapseToggle}
                >
                    {toggleButtonText}
                </button>
            </nav>
        </section>
    );
};
